import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { formatTimestamp, dateFromTicks } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import DataModal from './DataModal';
import './UnitDetails.css';

function UnitDetails() {
  const { unitName } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timestampedByID, setTimestampedByID] = useState({});
  const [plainByID, setPlainByID] = useState({});
  const [modalData, setModalData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const getSensorValues = (id) => {
    const isSingleValue = id.endsWith("_4");

    const sensorData = isSingleValue ? timestampedByID[id] : timestampedByID["s_" + id];
    let sensorValue = "No data";

    if (sensorData && sensorData.length > 0) {
      const latest = sensorData[sensorData.length - 1];
      try {
        const decodedData = latest["deserializedData"];
        if (isSingleValue) {
          sensorValue = decodedData === "\u0000" ? "off" : "on";
        }
        else {
          if (decodedData && decodedData.length > 0) {
            const values = [];
            for (let i = 0; i < decodedData.length; i++) {
              values.push(decodedData[i]["Avg"]);
            }
            sensorValue = values.join(", ");
            if (latest["id"].endsWith("_1")) {
              sensorValue = `${values[1]}°C ${values[0]}%`;
            } else if (latest["id"].endsWith("_3")) {
              sensorValue = `${values[1]} ppm`;
            }
          }
        }
      } catch (e) {
        sensorValue = "Error parsing data" + e;
      }

    }

    return sensorValue;
  }

  const getSensorTimestamp = (id) => {
    const isSingleValue = id.endsWith("_4");
    const sensorData = isSingleValue ? timestampedByID[id] : timestampedByID["s_" + id];

    if (sensorData && sensorData.length > 0) {
      const latest = sensorData[sensorData.length - 1];
      return latest.created;
    }

    return null;
  }

  const isTimestampOld = (timestamp) => {
    if (!timestamp) return false;

    const date = dateFromTicks(timestamp);
    if (isNaN(date.getTime())) return false;

    const now = new Date();
    const diffMinutes = (now - date) / (1000 * 60);

    return diffMinutes > 5;
  }

  const loadUnitData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [timestampedData, plainData] = await Promise.all([
        apiService.fetchTimestampedData(unitName, null, true),
        apiService.fetchPlainData(unitName, null, true)
      ]);

      // Group data by ID
      const timestampedGrouped = {};
      if (timestampedData.items && timestampedData.items.length > 0) {
        timestampedData.items.forEach(item => {
          if (!timestampedGrouped[item.id]) {
            timestampedGrouped[item.id] = [];
          }
          timestampedGrouped[item.id].push(item);
        });
      }

      const plainGrouped = {};
      if (plainData.items && plainData.items.length > 0) {
        plainData.items.forEach(item => {
          if (!plainGrouped[item.id]) {
            plainGrouped[item.id] = [];
          }
          plainGrouped[item.id].push(item);
        });
      }

      setTimestampedByID(timestampedGrouped);
      setPlainByID(plainGrouped);
    } catch (err) {
      setError(`Error loading data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnitData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitName]);

  const showDeserializedData = async (id) => {
    setModalData({ type: 'timestamped', id, unit: unitName, loading: true });
  };

  const showPlainDeserializedData = async (id) => {
    setModalData({ type: 'plain', id, unit: unitName, loading: true });
  };

  const closeModal = () => {
    setModalData(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadUnitData();
    setRefreshing(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const timestampedIDs = Object.keys(timestampedByID);
  const plainIDs = Object.keys(plainByID);

  return (
    <div className="unit-details-container">
      <div className="container">
        <div className="header-actions">
          <Link to="/" className="back-btn">← Back to Units</Link>
          <div className="right-actions">
            <span className="username">👤 {user?.username}</span>
            <button
              onClick={handleRefresh}
              className="refresh-btn"
              disabled={refreshing || loading}
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        {loading && <div className="loading">Loading unit data...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && (
          <>
            <div className="unit-info">
              <h2>{unitName}</h2>
            </div>


            {plainIDs.includes("sensors_config") && (
              <div className="table-container">
                <div className="stats">
                  {JSON.parse(plainByID["sensors_config"][plainByID["sensors_config"].length - 1]["deserializedData"])["Sensors"].map((sensor) => {
                    const sensorValue = getSensorValues(sensor.Id, timestampedByID);
                    const timestamp = getSensorTimestamp(sensor.Id);
                    const isOld = isTimestampOld(timestamp);
                    return (
                      <div className="stat-card" key={sensor.Id}>
                        <div className="stat-label">{sensor.Id} - {sensor.Name}</div>
                        <div className="stat-value">{sensorValue}</div>
                        <div className="stat-label" style={{ color: isOld ? 'red' : 'inherit' }}>
                          {formatTimestamp(timestamp)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            )}

            {timestampedIDs.length > 0 && (
              <div className="table-container">
                <h2 className="section-title">Timestamped Data</h2>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Records</th>
                      <th>Latest Created</th>
                      <th>Latest Updated</th>
                      <th>Data Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timestampedIDs.map(id => {
                      const items = timestampedByID[id];
                      const latest = items[items.length - 1];
                      return (
                        <tr key={id} onClick={() => showDeserializedData(id)}>
                          <td>{id}</td>
                          <td>{items.length}</td>
                          <td>{formatTimestamp(latest.created)}</td>
                          <td>{formatTimestamp(latest.updated)}</td>
                          <td>{latest.data ? latest.data.length : 0} bytes</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {plainIDs.length > 0 && (
              <div className="table-container">
                <h2 className="section-title">Plain Data</h2>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Records</th>
                      <th>Latest Created</th>
                      <th>Latest Updated</th>
                      <th>Data Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plainIDs.map(id => {
                      const items = plainByID[id];
                      const latest = items[items.length - 1];
                      return (
                        <tr key={id} onClick={() => showPlainDeserializedData(id)}>
                          <td>{id}</td>
                          <td>{items.length}</td>
                          <td>{formatTimestamp(latest.created)}</td>
                          <td>{formatTimestamp(latest.updated)}</td>
                          <td>{latest.data ? latest.data.length : 0} bytes</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {timestampedIDs.length === 0 && plainIDs.length === 0 && (
              <div className="empty-state">
                <h2>No Data Found</h2>
                <p>This unit has no timestamped or plain data.</p>
              </div>
            )}
          </>
        )}
      </div>

      {modalData && (
        <DataModal
          data={modalData}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default UnitDetails;

